import { useEffect, useState } from 'react';
import { useMondayContext } from '../../contexts/monday-context';
import { performMondayApiRequest } from '../../services/monday-service';
import getColumnValues from '../../queries/get-column-values';
import { boardSelectTypes } from './board-select-types';
import flatten from 'lodash/flatten';
import { getBoardOptionKey } from './use-board-select-options-service';

const NAME_COLUMN = 'name';

const getItemsColumnValues = async (itemIds) => {
  const { items } = await performMondayApiRequest(getColumnValues(itemIds));

  return items.map(({ name, column_values, board }) => ({
    board,
    name,
    columns: [{ id: NAME_COLUMN, type: NAME_COLUMN, text: name, title: NAME_COLUMN }, ...column_values],
  }));
};

const transformColumns = (columns, boardId, itemId) => {
  return columns
    .filter(({ type }) => boardSelectTypes[type])
    .map(({ title, text, id, type }) => ({
      label: text,
      type,
      title,
      id,
      value: getBoardOptionKey(boardId, id, itemId),
      icon: boardSelectTypes[type],
    }));
};

export default function useBoardSelectOptions() {
  const {
    mondayContext: { itemId },
  } = useMondayContext();
  const [boardSelectOptions, setBoardSelectOptions] = useState([]);

  useEffect(() => {
    let isSubscribed = true;

    const getBoardSelectOptions = async () => {
      const currentItemWithColumnValues = await getItemsColumnValues([itemId]);

      const currentBoardOptions = {
        label: `${currentItemWithColumnValues[0].board.name} board`,
        boardId: currentItemWithColumnValues[0].board.id,
        options: transformColumns(currentItemWithColumnValues[0].columns, currentItemWithColumnValues[0].board.id),
      };

      const boardRelationsColumns = currentItemWithColumnValues[0].columns.filter(
        ({ type }) => type === 'board-relation'
      );

      const optionsFromBoardRelationsColumns = await Promise.all(
        boardRelationsColumns.map(async (boardRelationsColumn) => {
          const itemsIds =
            JSON.parse(boardRelationsColumn.value || '{}').linkedPulseIds?.map(({ linkedPulseId }) => linkedPulseId) ||
            [];

          const itemsFromBoardRelation = await getItemsColumnValues(itemsIds);

          return itemsFromBoardRelation.map((item, index) => {
            return {
              label: `${item.board.name} board - ${item.name}`,
              boardId: item.board.id,
              itemId: itemsIds[index],
              options: transformColumns(item.columns, item.board.id, itemsIds[index]),
            };
          });
        })
      );

      if (!isSubscribed) return;

      setBoardSelectOptions([currentBoardOptions, ...flatten(optionsFromBoardRelationsColumns)]);
    };

    getBoardSelectOptions();

    return () => (isSubscribed = false);
  }, [itemId]);

  return { boardSelectOptions };
}
